import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Box,
  Button,
  ComboBox,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  LoadingOrbit,
  PageHeader,
  Text,
  TextField,
  useToast,
  type ComboBoxOption,
} from '@chatsdet/ui';
import {
  EnvelopeSimple,
  Link,
  PencilSimple,
  Plus,
  Trash,
  UserPlus,
  X,
} from '@phosphor-icons/react';
import {
  superadminMarketingLeadsRoute,
  type SuperadminMarketingLeadsSearchParams,
} from '../../../router';
import { CopyableEmail } from './CopyableEmail';
import {
  useCreateMarketingLeadAccountCardMutation,
  useLinkMarketingLeadAccountCardMutation,
  useMarketingLeadAccountCardsQuery,
  useMarketingLeadsQuery,
  useUnlinkMarketingLeadAccountCardMutation,
  useUpdateMarketingLeadPreferredEmailMutation,
  type MarketingLead,
} from './useMarketingLeads';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function getEmailDomain(email: string) {
  return email.split('@')[1]?.toLowerCase() ?? '';
}

function getAccountDefaults(email: string) {
  const domain = getEmailDomain(email);
  return {
    title: domain || email,
    companyName: domain,
    domain,
  };
}

function PreferredEmailCell({ lead }: { lead: MarketingLead }) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [value, setValue] = React.useState(lead.preferredEmail ?? '');
  const mutation = useUpdateMarketingLeadPreferredEmailMutation();
  const { toast } = useToast();

  React.useEffect(() => {
    if (!isEditing) setValue(lead.preferredEmail ?? '');
  }, [isEditing, lead.preferredEmail]);

  const trimmedValue = value.trim();
  const errorMessage =
    trimmedValue && !EMAIL_PATTERN.test(trimmedValue) ? 'Enter a valid email address.' : undefined;

  if (!isEditing) {
    return (
      <Flex align="center" gap="xs" className="min-w-0">
        <Text className={lead.preferredEmail ? '' : 'text-muted-foreground'}>
          {lead.preferredEmail ?? 'Not set'}
        </Text>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip={lead.preferredEmail ? 'Edit preferred email' : 'Add preferred email'}
          onPress={() => setIsEditing(true)}
        >
          <PencilSimple size={14} />
        </Button>
      </Flex>
    );
  }

  const save = () => {
    if (errorMessage) return;
    mutation.mutate(
      { id: lead.id, preferredEmail: trimmedValue || null },
      {
        onSuccess: () => {
          toast.success('Preferred email updated');
          setIsEditing(false);
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  return (
    <Flex align="start" gap="xs" className="min-w-[260px]">
      <TextField
        aria-label={`Preferred email for ${lead.email}`}
        type="email"
        value={value}
        onChange={setValue}
        errorMessage={errorMessage}
        placeholder="name@company.com"
        className="min-w-0 flex-1"
        isDisabled={mutation.isPending}
        autoFocus
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') setIsEditing(false);
        }}
      />
      <Flex gap="xs" className="pt-0.5">
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Save preferred email"
          onPress={save}
          isDisabled={Boolean(errorMessage) || mutation.isPending}
        >
          <span aria-hidden>✓</span>
        </Button>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Cancel editing"
          onPress={() => setIsEditing(false)}
          isDisabled={mutation.isPending}
        >
          <X size={14} />
        </Button>
      </Flex>
    </Flex>
  );
}

function AccountCardDialog({ lead, onClose }: { lead: MarketingLead | null; onClose: () => void }) {
  const isOpen = Boolean(lead);
  const [search, setSearch] = React.useState('');
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [domain, setDomain] = React.useState('');
  const { toast } = useToast();
  const cardsQuery = useMarketingLeadAccountCardsQuery(search, isOpen);
  const linkMutation = useLinkMarketingLeadAccountCardMutation();
  const createMutation = useCreateMarketingLeadAccountCardMutation();
  const unlinkMutation = useUnlinkMarketingLeadAccountCardMutation();

  React.useEffect(() => {
    if (!lead) return;
    const defaults = getAccountDefaults(lead.email);
    setSearch('');
    setSelectedAccountId(lead.accountCard?.id ?? null);
    setIsCreating(false);
    setTitle(defaults.title);
    setCompanyName(lead.accountCard?.companyName ?? defaults.companyName);
    setDomain(lead.accountCard?.domain ?? defaults.domain);
  }, [lead?.id]);

  const options: ComboBoxOption[] = React.useMemo(
    () =>
      (cardsQuery.data?.data ?? []).map((card) => ({
        id: card.id,
        label: card.companyName || card.title,
        description: card.domain || card.title,
        textValue: [card.companyName, card.title, card.domain].filter(Boolean).join(' '),
      })),
    [cardsQuery.data?.data]
  );

  const isPending = linkMutation.isPending || createMutation.isPending || unlinkMutation.isPending;
  const canCreate = title.trim().length > 0 && !isPending;

  const handleLink = () => {
    if (!lead || !selectedAccountId) return;
    linkMutation.mutate(
      { leadId: lead.id, accountCardId: selectedAccountId },
      {
        onSuccess: () => {
          toast.success('Account linked');
          onClose();
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  const handleCreate = () => {
    if (!lead || !canCreate) return;
    createMutation.mutate(
      {
        leadId: lead.id,
        title: title.trim(),
        companyName: companyName.trim(),
        domain: domain.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Account created and linked');
          onClose();
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  const handleUnlink = () => {
    if (!lead) return;
    unlinkMutation.mutate(lead.id, {
      onSuccess: () => {
        toast.success('Account unlinked');
        onClose();
      },
      onError: (error) => toast.error(error.message),
    });
  };

  if (!lead) return null;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <span />
      <Dialog
        title={lead.accountCard ? 'Manage linked account' : 'Link an account'}
        description={`Choose an existing account for ${lead.email}, or create a new prospect account.`}
      >
        <DialogContent>
          <ComboBox
            label="Existing account"
            placeholder={cardsQuery.isLoading ? 'Loading accounts…' : 'Search account cards…'}
            items={options}
            inputValue={search}
            onInputChange={setSearch}
            selectedKey={selectedAccountId}
            onSelectionChange={(key) => setSelectedAccountId(key ? String(key) : null)}
            isDisabled={isPending || isCreating}
            isLoading={cardsQuery.isFetching}
            renderItem={(item) => (
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{item.label}</span>
                {item.description && (
                  <span className="truncate text-xs text-muted-foreground">{item.description}</span>
                )}
              </div>
            )}
          />

          <Flex align="center" justify="between" gap="sm" className="border-t border-border pt-4">
            <Text size="sm" className="text-muted-foreground">
              {isCreating
                ? 'New accounts start in Prospect.'
                : 'Already have an account card? Select it above.'}
            </Text>
            <Button
              variant="outline"
              size="sm"
              onPress={() => setIsCreating((value) => !value)}
              isDisabled={isPending}
              iconLeft={isCreating ? <X size={14} /> : <Plus size={14} />}
            >
              {isCreating ? 'Choose existing' : 'Create new account'}
            </Button>
          </Flex>

          {isCreating && (
            <Flex direction="column" gap="sm">
              <TextField label="Title" value={title} onChange={setTitle} isRequired />
              <TextField label="Company name" value={companyName} onChange={setCompanyName} />
              <TextField
                label="Domain"
                value={domain}
                onChange={setDomain}
                placeholder="example.com"
              />
            </Flex>
          )}
        </DialogContent>
        <DialogFooter>
          {lead.accountCard && (
            <Button
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              onPress={handleUnlink}
              isDisabled={isPending}
              iconLeft={<Trash size={15} />}
            >
              Unlink
            </Button>
          )}
          <Button variant="outline" onPress={onClose} isDisabled={isPending}>
            Cancel
          </Button>
          {isCreating ? (
            <Button variant="primary" onPress={handleCreate} isDisabled={!canCreate}>
              {createMutation.isPending ? 'Creating…' : 'Create and link'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onPress={handleLink}
              isDisabled={!selectedAccountId || isPending}
            >
              {linkMutation.isPending ? 'Linking…' : 'Link account'}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}

export function SuperadminMarketingLeads() {
  const searchParams = superadminMarketingLeadsRoute.useSearch();
  const navigate = useNavigate();
  const [manageLead, setManageLead] = React.useState<MarketingLead | null>(null);

  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? 20;
  const search = searchParams.search ?? '';
  const leadsQuery = useMarketingLeadsQuery({ page, pageSize, search: search || undefined });

  const updateSearch = React.useCallback(
    (updates: Record<string, unknown>) => {
      void navigate({
        to: '/superadmin/marketing-leads',
        search: (previous: SuperadminMarketingLeadsSearchParams) => ({ ...previous, ...updates }),
      });
    },
    [navigate]
  );

  const columns: ColumnDef<StockFeatures, MarketingLead>[] = React.useMemo(
    () => [
      {
        accessorKey: 'email',
        header: 'Signup email',
        cell: ({ row }) => <CopyableEmail email={row.original.email} />,
        meta: { flex: 1.3 },
      },
      {
        id: 'preferredEmail',
        header: 'Preferred email',
        cell: ({ row }) => <PreferredEmailCell lead={row.original} />,
        meta: { flex: 1.5 },
      },
      {
        id: 'company',
        header: 'Company',
        cell: ({ row }) => {
          const account = row.original.accountCard;
          if (!account) {
            return (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onPress={() => setManageLead(row.original)}
                iconLeft={<Link size={14} />}
              >
                Link account
              </Button>
            );
          }
          return (
            <Flex direction="column" className="min-w-0">
              <Text weight="medium" className="truncate">
                {account.companyName || account.title}
              </Text>
              {account.domain && (
                <Text size="xs" className="truncate text-muted-foreground">
                  {account.domain}
                </Text>
              )}
            </Flex>
          );
        },
        meta: { flex: 1.2 },
      },
      {
        accessorKey: 'source',
        header: 'Source',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.source || '—'}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Signed up',
        cell: ({ row }) => (
          <Text size="sm" className="whitespace-nowrap text-muted-foreground tabular-nums">
            {formatDate(row.original.createdAt)}
          </Text>
        ),
        meta: { flex: 0.9 },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip={row.original.accountCard ? 'Manage account link' : 'Link account'}
            onPress={() => setManageLead(row.original)}
          >
            <UserPlus size={16} />
          </Button>
        ),
        meta: { flex: 0.35, minWidth: 44, maxWidth: 56 },
      },
    ],
    []
  );

  const pagination = leadsQuery.data?.pagination;

  return (
    <Box className="space-y-2">
      <PageHeader
        icon={<EnvelopeSimple size={24} />}
        title="Marketing Leads"
        description="Review marketing signups and connect them to account cards. Newest signups appear first."
      />

      {leadsQuery.isLoading && !leadsQuery.data ? (
        <Flex align="center" justify="center" className="py-12">
          <LoadingOrbit size="lg" />
        </Flex>
      ) : leadsQuery.isError ? (
        <Text className="text-destructive">
          {leadsQuery.error instanceof Error
            ? leadsQuery.error.message
            : 'Failed to load marketing leads'}
        </Text>
      ) : (
        <DataTable
          columns={columns}
          data={leadsQuery.data?.data ?? []}
          aria-label="Marketing leads"
          searchable
          searchPlaceholder="Search emails, companies, or sources…"
          searchValue={search}
          onSearchChange={(value) => updateSearch({ search: value || undefined, page: 1 })}
          getRowId={(row) => row.id}
          serverPagination={
            pagination
              ? {
                  page,
                  pageSize,
                  pageCount: pagination.totalPages,
                  totalItems: pagination.totalItems,
                  onPageChange: (nextPage) => updateSearch({ page: nextPage }),
                  onPageSizeChange: (nextPageSize) =>
                    updateSearch({ pageSize: nextPageSize, page: 1 }),
                  pageSizeOptions: [20, 50, 100],
                  isLoading: leadsQuery.isFetching,
                }
              : undefined
          }
        />
      )}

      <AccountCardDialog lead={manageLead} onClose={() => setManageLead(null)} />
    </Box>
  );
}
